/**
 * TPRA Home Tasks API Request Hook
 * Fetches list of actionable tasks for the dashboard
 */

import { get } from "@/infra/rest";
import { APIEndpoints } from "@/shared/constants/Endpoints";
import { tpraHomeQueryKeys } from "@/shared/constants/QueryKeys";
import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import type { TasksResponse } from "../types";
import { TasksResponseSchema } from "../validation/schemas";

/**
 * Query parameters for tasks API
 */
interface TasksQueryParams {
  show_all?: boolean;
}

/**
 * Fetches tasks data from the API
 * @param params - Optional query parameters
 * @returns Validated tasks response
 * @throws Error if API request fails or response validation fails
 */
async function fetchTasks(params?: TasksQueryParams): Promise<TasksResponse> {
  const response = await get<TasksResponse>({
    url: APIEndpoints.TPRAHomeTasks,
    config: { params },
  });

  // Validate response with Zod schema
  const validated = TasksResponseSchema.parse(response);

  return validated;
}

/**
 * React Query hook for fetching TPRA home tasks
 *
 * Provides:
 * - Automatic caching with TanStack Query
 * - Loading and error states
 * - Automatic refetching on mount
 * - Retry logic for failed requests
 *
 * @param showAll - Whether to fetch all tasks or just the limited set (default: false)
 * @returns React Query result with tasks data
 *
 * @example
 * ```typescript
 * function TasksSection({ showAll }) {
 *   const { data, isLoading, isError, refetch } = useGetTasks(showAll);
 *
 *   if (isLoading) return <TasksSkeleton />;
 *   if (isError) return <SectionError onRetry={refetch} />;
 *
 *   return <TasksList tasks={data.tasks} />;
 * }
 * ```
 */
export function useGetTasks(showAll = false): UseQueryResult<TasksResponse, Error> {
  return useQuery({
    queryKey: [...tpraHomeQueryKeys.tasks(), { show_all: showAll }],
    queryFn: () => fetchTasks(showAll ? { show_all: true } : undefined),

    // Caching Strategy
    staleTime: 0, // Always refetch for fresh data
    gcTime: 10 * 60 * 1000, // 10 minutes cache retention

    // Retry Strategy (exponential backoff)
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 5000),

    // Refetch Behavior
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    refetchOnReconnect: true,
  });
}
